import React, { useEffect, useRef, useState } from 'react'

import VolumeOverallTimer from './Components/VolumeOverallTimer'
import VolumeEvaluationTimer from './Components/VolumeEvaluationTimer'
import VolumeCheckTeacherModal from './Components/VolumeCheckTeacherModal'
import handleTeacherModeSpeech from './functions/handleTeacherModeSpeech'
import handleStudentModeSpeech from './functions/handleStudentModeSpeech'
import useDetectStudentDidSaySomething from './hooks/useDetectStudentDidSaySomething'
import { PROGRESS_STATES, SPEECH_VOLUME_CHECK_STATES, PROGRESS_STATES_ORDER, MAX_COUNT_TO_REMIND_STUDENT_TO_SPEAK, MS_TO_WAIT_BETWEEN_REMIND_STUDENT_TO_SPEAK, VOLUME_CHECK_EVENTS } from './StudentVolumeChecksConstants'
import { COPY_STRINGS } from '../../../constants/translatedCopy'
import { getCopyByGradeband } from '../../../services/ThemingService'
import { amiraGestures } from '../../Coach/GesturedSpeech'

import TestButton from '../Components/TestButton'
import useStudentPreChecks from '../useStudentPreChecks'
import useASRStoryPreload from '../hooks/useASRStoryPreload'
import VolumeMeter from '../Components/VolumeMeter/VolumeMeter'
import { PRE_CHECK_MACHINE_STATES } from '../constants'
import useControlledSpeech from '../Components/hooks/useControlledSpeech'
import StudentInstruction from '../Components/StudentInstruction'
import VolumeCheckSpot from './Components/VolumeCheckSpot'
import { useVolumeCheckSpot } from './Components/hooks/useVolumeCheckSpot'
import { getContentByGradeBand } from '../../../services/ThemingService'

export default function VolumeCheckModes(props) {

    /**
     * props
     */
    const { speechProps, toggleReaderContainer, showMicIcon, teacherCalled, teacherTestSuccessful, isTeacherMode, toggleTeacherMode, logVolumeCheckFailure } = props

    /**
    * states
    */
    const [volumeStats, getVolumeStats] = useState({})
    const [currentProgressState, setCurrentProgressState] = useState(PROGRESS_STATES.INITIAL)
    const [showTeacherTroubleshootScreen, setShowTeacherTroubleshootScreen] = useState(false)
    const [currentSpeechVolumeCheckState, setCurrentSpeechVolumeCheckState] = useState(SPEECH_VOLUME_CHECK_STATES.INITIAL)
    const [acceptedAtKindOfLoudVolume, setAcceptedAtKindOfLoudVolume] = useState(false)

    /**
     * hooks
     */
    const { speakWith } = useControlledSpeech({ speechProps })
    const { studentDidSaySomething, resetDidSaySomething } = useDetectStudentDidSaySomething({ volumeStats })
    const studentPreCheck = useStudentPreChecks({ itemToCheck: PRE_CHECK_MACHINE_STATES.studentSpeechVolumeChecks });
    const { preloadedStoryId } = useASRStoryPreload()
    const spotHook = useVolumeCheckSpot(speechProps)

    const { isCurrent, settings } = studentPreCheck;

    /**
     * mutable values, remember to use refVariable.current to access or mutate the value!
     */
    const studentSpeechVolumeEvaluationCount = useRef(0)
    const remindInitialSpeechTimer = useRef()
    const remindStudentCount = useRef(0)
    const timerId = useRef(0)
    const stepThroughProgressStatesInProgress = useRef(false)

    /**
     * Wait to start the volume check
     */
    useEffect(() => {
        // Reset progress flag when state changes
        stepThroughProgressStatesInProgress.current = false;
        
        if (isCurrent) {
            // Add a timeout safeguard to ensure stepThroughProgressStates executes
            // even if abortSpeech callback mechanism fails due to race conditions
            const safeguardTimeout = setTimeout(() => {
                console.warn('Volume check safeguard timeout triggered - executing stepThroughProgressStates manually');
                stepThroughProgressStates();
            }, 2000); // 2 second timeout

            speechProps.abortSpeech(() => {
                clearTimeout(safeguardTimeout);
                stepThroughProgressStates();
            });

            return () => {
                clearTimeout(safeguardTimeout);
                stepThroughProgressStatesInProgress.current = false;
                speechProps.abortSpeech();
            };
        }

        return () => {
            stepThroughProgressStatesInProgress.current = false;
            speechProps.abortSpeech()
        }
    }, [currentProgressState])

    /**
     * Waiting to start the volume check evaluation step
     */
    useEffect(() => {
        if (studentDidSaySomething.current && currentSpeechVolumeCheckState === SPEECH_VOLUME_CHECK_STATES.WAITING_FOR_INITIAL_SPEECH) {
            console.log("Volume activity started, student said something detected")
            setCurrentSpeechVolumeCheckState(SPEECH_VOLUME_CHECK_STATES.ACTIVITY_IN_PROGRESS)
        }
    }, [studentDidSaySomething.current, currentSpeechVolumeCheckState])

    /**
     * Handle reminder timer for initial speech
     */
    useEffect(() => {        
        if (currentSpeechVolumeCheckState === SPEECH_VOLUME_CHECK_STATES.WAITING_FOR_INITIAL_SPEECH) {
            remindStudentCount.current = 0
            startRemindInitialSpeechTimer()
        }

        return () => {
            cancelRemindInitialSpeechTimer()
        }
    }, [currentSpeechVolumeCheckState, isTeacherMode])

    /**
     * Timer methods
     */
    const startRemindInitialSpeechTimer = async () => {
        cancelRemindInitialSpeechTimer()

        const remind = () => {
            reminderSpeech()
        }
        if(remindStudentCount.current <= MAX_COUNT_TO_REMIND_STUDENT_TO_SPEAK){
            if(isTeacherMode){
                studentPreCheck.logEvent(VOLUME_CHECK_EVENTS.TEACHER_PROMPT_TO_SPEAK)
            }else{
                studentPreCheck.logEvent(VOLUME_CHECK_EVENTS.STUDENT_PROMPT_TO_SPEAK)
            }

            const delay = MS_TO_WAIT_BETWEEN_REMIND_STUDENT_TO_SPEAK
            timerId.current += 1
            const currentTimerId = timerId.current
            remindInitialSpeechTimer.current = setTimeout(() => {
                remind()
            }, delay)
        } 
    }

    const cancelRemindInitialSpeechTimer = async () => {
        if (remindInitialSpeechTimer.current) {
            clearTimeout(remindInitialSpeechTimer.current)
            remindInitialSpeechTimer.current = null
        }
    }

    const reminderSpeech = async () => {
        const studentChecks = getCopyByGradeband(COPY_STRINGS, 'student_checks');
        if(isTeacherMode){
            if(remindStudentCount.current === 0){
                await speechProps.speakWithPromise({textToSpeak: studentChecks.handle_no_audio_one})
                await speechProps.speakWithPromise({ textToSpeak: studentChecks.handle_no_audio_part_two })
                await speechProps.speakWithPromise({ textToSpeak: studentChecks.wake_up_spot })
                startRemindInitialSpeechTimer()
            }else{
                await speechProps.speakWithPromise({ textToSpeak: studentChecks.teacher_mode_volume_failure_one })
                setCurrentSpeechVolumeCheckState(SPEECH_VOLUME_CHECK_STATES.INITIAL)
                setShowTeacherTroubleshootScreen(true)
            }
        }else{
            if (remindStudentCount.current === 0) {
                await speechProps.speakWithPromise({ textToSpeak: studentChecks.handle_no_audio_one })
                await speechProps.speakWithPromise({ textToSpeak: studentChecks.handle_no_audio_part_two })
                speechProps.playDance({
                    gesture: amiraGestures.SweepWave
                })
                await speechProps.speakWithPromise({ textToSpeak: studentChecks.wake_up_spot })
                startRemindInitialSpeechTimer()
            } else if (remindStudentCount.current === 1) {
                await speechProps.speakWithPromise({ textToSpeak: studentChecks.handle_no_audio_two })
                startRemindInitialSpeechTimer()
            }
            else if (remindStudentCount.current === 2) {
                await speechProps.speakWithPromise({ textToSpeak: studentChecks.handle_get_teacher })
                setCurrentSpeechVolumeCheckState(SPEECH_VOLUME_CHECK_STATES.INITIAL)
                setShowTeacherTroubleshootScreen(true)
            }
        }
        
        remindStudentCount.current = remindStudentCount.current + 1
    }

    /**
     * Methods
     */
    const stepThroughProgressStates = async () => {
        // Prevent duplicate executions during race conditions
        if (stepThroughProgressStatesInProgress.current) {
            console.log('stepThroughProgressStates already in progress, skipping duplicate call');
            return;
        }
        
        stepThroughProgressStatesInProgress.current = true;
        
        try {
            if (isTeacherMode) {
                teacherCalled.current = true
                await handleTeacherModeSpeech({ speechProps, currentProgressState, settings, goToNextState, setCurrentSpeechVolumeCheckState, acceptedAtKindOfLoudVolume, toggleReaderContainer, showMicIcon, setShowTeacherTroubleshootScreen, spotHook })
            } else {
                await handleStudentModeSpeech({ speakWith, speechProps, currentProgressState, settings, goToNextState, setCurrentSpeechVolumeCheckState, acceptedAtKindOfLoudVolume, toggleReaderContainer, showMicIcon, teacherCalled, setCurrentProgressState, spotHook })
            }

            if (currentProgressState === PROGRESS_STATES.SPOT_EXIT_SCREEN) {
                finishCheck()
            }
        } catch (err) {
            console.error('Volume check stepThroughProgressStates failed:', err);
            studentPreCheck.failedCheck(new Error("Volume check failed", { cause: err }))
        } finally {
            stepThroughProgressStatesInProgress.current = false;
        }
    }

    /**
     * This method should only be called once when it is finished everything.
     */
    const finishCheck = async () => {
        setCurrentSpeechVolumeCheckState(SPEECH_VOLUME_CHECK_STATES.ACTIVITY_COMPLETED)
        studentPreCheck.finishCheck()
    }
    const onAcceptableVolumeCheckPassed = (options) => {
        if (isTeacherMode) {
            teacherTestSuccessful.current = true
        }
        if(options && options.loudButAcceptable){
            setAcceptedAtKindOfLoudVolume(true)
        }
        

        
        goToNextState()
    }
    const retry = () => {
        if (speechProps?.resetRecorderErrorLogs) {
            speechProps.resetRecorderErrorLogs();
        }
        resetStatesToBeginningOfCheck()
        studentPreCheck.retryWith(stepThroughProgressStates)
    }
    const resetStatesToBeginningOfCheck = () => {
        studentSpeechVolumeEvaluationCount.current = 0
        setCurrentSpeechVolumeCheckState(SPEECH_VOLUME_CHECK_STATES.INITIAL)
        resetDidSaySomething()
        setShowTeacherTroubleshootScreen(false)
    }
    const goToNextState = () => {
        const currentProgressStateIndex = PROGRESS_STATES_ORDER.indexOf(currentProgressState)
        if (currentProgressStateIndex < PROGRESS_STATES_ORDER.length - 1) {
            setCurrentProgressState(PROGRESS_STATES_ORDER[currentProgressStateIndex + 1])
        }
    }

    /**
     * start rendering
     */
    if (!studentPreCheck.isCurrent) return null

    /**
     * handle unexpected errors
     */
    if (studentPreCheck.currentError) {
        return <StudentInstruction>
            <p>Something went wrong with your speech volume</p>
            <TestButton action="Retry" currentCheck={PRE_CHECK_MACHINE_STATES.studentSpeechVolumeChecks} retry={retry} />
        </StudentInstruction>
    }

    /**
     * calling teacher for help / troubleshoot screens
     */
    if (showTeacherTroubleshootScreen) {
        return <VolumeCheckTeacherModal
            volumeStats={volumeStats}
            studentDidSaySomething={studentDidSaySomething}
            handleRetry={retry}
            isTeacherMode={isTeacherMode}
            toggleTeacherMode={toggleTeacherMode}
            teacherTestSuccessful={teacherTestSuccessful}
            logVolumeCheckFailure={logVolumeCheckFailure} />
    }

    // iPad required the volume meter to be rendered at all times to work
    const waitingForEvaluation = currentSpeechVolumeCheckState === SPEECH_VOLUME_CHECK_STATES.ACTIVITY_IN_PROGRESS
    const waitingForInitialSpeech = currentProgressState === PROGRESS_STATES.SPOT_ON_SCREEN_2 && currentSpeechVolumeCheckState === SPEECH_VOLUME_CHECK_STATES.WAITING_FOR_INITIAL_SPEECH && !studentDidSaySomething.current
    const shouldRenderVolumeMeter = !speechProps.isSpeaking && (currentSpeechVolumeCheckState === SPEECH_VOLUME_CHECK_STATES.WAITING_FOR_INITIAL_SPEECH || currentSpeechVolumeCheckState === SPEECH_VOLUME_CHECK_STATES.ACTIVITY_IN_PROGRESS)
    
    /**
     * the main volume check components
     */
    return <React.Fragment>
        <VolumeOverallTimer {...{ speechProps, setShowTeacherTroubleshootScreen, volumeStats, studentSpeechVolumeEvaluationCount, logVolumeCheckFailure }} />
        {waitingForEvaluation && <VolumeEvaluationTimer {...{ speechProps, volumeStats, studentSpeechVolumeEvaluationCount, isTeacherMode, setShowTeacherTroubleshootScreen, onAcceptableVolumeCheckPassed, setCurrentSpeechVolumeCheckState, resetDidSaySomething, logVolumeCheckFailure }} />}
        <StudentInstruction>
            {isTeacherMode && getContentByGradeBand({
                high: <p>When the microphone turns green, say "Testing, 1, 2, 3"</p>,
                defualt: <p>When the microphone turns green, say "Wake up, Spot"</p>,
            })}
            <VolumeCheckSpot state={spotHook.spotState}/>
        </StudentInstruction>
        {!showTeacherTroubleshootScreen && shouldRenderVolumeMeter &&
            <VolumeMeter
                key={currentProgressState} // This is important for the recorder stats to work on iOS, it required at least 1 rerender of this component, really need to get the to bottom of it though.
                speechProps={speechProps}
                getVolumeStats={getVolumeStats}
                shouldStartVolumeProcessing={isCurrent} />}
    </React.Fragment>
}